import { useState, useEffect } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { GraduationCap, RefreshCw, CheckCircle2, Clock, Award, Star, Calendar, ChevronRight } from "lucide-react";

const statusColors = {
  "not_ready": { bg: "bg-slate-50", text: "text-slate-400", label: "Not Ready" },
  "model_approved": { bg: "bg-[#FEF3CD]", text: "text-[#856404]", label: "Model Approved" },
  "supervised": { bg: "bg-[#CCE5FF]", text: "text-[#004085]", label: "Supervised" },
  "independent": { bg: "bg-[#D4EDDA]", text: "text-[#155724]", label: "Independent" },
  "certified": { bg: "bg-slate-900", text: "text-white", label: "Certified" },
};

export default function StylistTrainingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/portal").then(res => {
      setData(res.data?.training || {});
    }).catch(() => toast.error("Failed to load training")).finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;
  }

  const t = data || {};
  const approvals = t.approvals || [];
  const evals = t.recent_evaluations || [];
  const upcoming = t.upcoming_sessions || [];

  return (
    <div data-testid="stylist-training-page" className="p-6 lg:p-8 max-w-[1100px] mx-auto space-y-6">
      <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">My Training</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
          <GraduationCap className="w-5 h-5 text-[#8B5CF6] mx-auto mb-1" />
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{t.completed_sessions_count || 0}</p>
          <p className="text-xs text-slate-400">Sessions Done</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
          <Star className="w-5 h-5 text-[#F59E0B] mx-auto mb-1" />
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{t.avg_score || 0}</p>
          <p className="text-xs text-slate-400">Avg Eval Score</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
          <Award className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{t.total_evaluations || 0}</p>
          <p className="text-xs text-slate-400">Evaluations</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
          <Calendar className="w-5 h-5 text-[#EC4899] mx-auto mb-1" />
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{upcoming.length}</p>
          <p className="text-xs text-slate-400">Upcoming</p>
        </div>
      </div>

      {/* Service Approvals */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-['Outfit'] font-semibold text-slate-900 mb-3">Service Certifications</h2>
        {approvals.length === 0 ? (
          <p className="text-sm text-slate-400">No service approvals yet</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {approvals.map((a, i) => {
              const sc = statusColors[a.status] || statusColors.not_ready;
              return (
                <div key={a.track_id || a.service_name || `approval-${i}`} data-testid={`approval-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                  <div className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${sc.bg} ${sc.text}`}>{sc.label}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{a.service_name || a.track_name || "Service"}</p>
                    <p className="text-xs text-slate-400">{a.skill_level || ""}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upcoming Sessions */}
      {upcoming.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-['Outfit'] font-semibold text-slate-900 mb-3">Upcoming Sessions</h2>
          <div className="space-y-2">
            {upcoming.map((s, i) => (
              <div key={s.session_id || s.id || `upcoming-${i}`} data-testid={`upcoming-session-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                <div className="w-10 h-10 rounded-lg bg-[#FAFAFA] flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-slate-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{s.title || s.track_name || "Session"}</p>
                  <p className="text-xs text-slate-400">{fmtDate(s.scheduled_date || s.date)} &middot; {s.session_type || ""}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Evaluations */}
      {evals.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-['Outfit'] font-semibold text-slate-900 mb-3">Recent Evaluations</h2>
          <div className="space-y-2">
            {evals.map((e, i) => (
              <div key={e.evaluation_id || e.id || `eval-${i}`} data-testid={`eval-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                  e.overall_score >= 80 ? "bg-[#D4EDDA] text-[#155724]" :
                  e.overall_score >= 60 ? "bg-[#FEF3CD] text-[#856404]" :
                  "bg-[#FDE8E8] text-rose-600"
                }`}>
                  {e.overall_score}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{e.track_name || e.service_name || "Evaluation"}</p>
                  <p className="text-xs text-slate-400">{e.result || ""} &middot; {fmtDate(e.created_at)}</p>
                </div>
                {e.strengths && e.strengths.length > 0 && (
                  <span className="text-xs text-emerald-600 bg-[#D4EDDA] px-2 py-0.5 rounded-full hidden sm:inline">{e.strengths[0]}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
